import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ProspectingListConfig,
  ProspectingService,
  SaveListResult,
  apiErrorMessage,
} from '../../../../services/prospecting.service';

/** "Save as ICP" for a prospecting list. */
@Component({
  selector: 'app-save-list-icp-dialog',
  standalone: true,
  imports: [NgIf, FormsModule],
  templateUrl: './save-list-icp-dialog.component.html',
})
export class SaveListIcpDialogComponent implements OnInit, OnDestroy {
  @Input({ required: true }) listId!: string;
  @Input() config: ProspectingListConfig | null = null;
  @Output() saved = new EventEmitter<SaveListResult>();
  @Output() closed = new EventEmitter<void>();

  name = '';
  setDefault = false;
  isSaving = false;
  errorMessage = '';

  private destroy$ = new Subject<void>();

  constructor(private prospectingService: ProspectingService) {}

  ngOnInit(): void {
    const summary = this.config?.summary_label?.trim() || 'Advanced Prospecting';
    this.name = `Prospecting – ${summary}`.slice(0, 190);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    if (this.isSaving) {
      return;
    }
    this.closed.emit();
  }

  save(): void {
    const name = this.name.trim();
    if (this.isSaving) {
      return;
    }
    if (!name) {
      this.errorMessage = 'Give the ICP a name.';
      return;
    }

    this.errorMessage = '';
    this.isSaving = true;

    this.prospectingService.saveAsIcp(this.listId, name, this.setDefault, this.config)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isSaving = false;
          this.saved.emit(result);
        },
        error: (error) => {
          this.isSaving = false;
          if (error?.status === 409) {
            this.errorMessage = apiErrorMessage(error, 'Wait for the running generation to finish, then save.');
            return;
          }
          if (error?.status === 422) {
            const errors = error?.error?.errors;
            const first = errors ? Object.values(errors).flat()[0] : null;
            this.errorMessage = (typeof first === 'string' && first) || apiErrorMessage(error, 'Check the ICP name and try again.');
            return;
          }
          this.errorMessage = apiErrorMessage(error, 'Could not save the list as an ICP.');
        }
      });
  }
}
